import { NavLink } from 'react-router-dom';
import { X } from 'lucide-react';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { navSections, type NavItem } from '@/config/navigation';
import { InboxAlertsBubble } from '@/features/alerts/InboxAlertsBubble';
import type { InboxAlertas } from '@/features/alerts/alerts-format';
import { env } from '@/lib/env';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/routes/paths';
import { TenantSwitcher } from './TenantSwitcher';

/**
 * Navegación lateral.
 *
 * Replica la estructura de la referencia visual: sidebar blanco de 240px pegado al borde
 * izquierdo, logo arriba, ítems con icono + etiqueta, el activo en azul de marca sólido
 * con texto blanco, y un grupo secundario separado por una línea.
 *
 * En móvil no se pierde: se convierte en un drawer que entra desde la izquierda
 * (lo controla `AppLayout` con el botón de hamburguesa del header).
 *
 * «Conversaciones» lleva la burbuja de mensajes sin ver: es el aviso que se ve mientras el asesor
 * trabaja en otra pantalla (el mismo número que la burbuja del header, ver `features/alerts`).
 */

export interface SidebarProps {
  /** Estado del drawer en móvil (en `lg` siempre está visible). */
  mobileOpen: boolean;
  onCloseMobile: () => void;
  /** Avisos del inbox: burbuja en «Conversaciones». */
  alertas: InboxAlertas;
}

export function Sidebar({ mobileOpen, onCloseMobile, alertas }: SidebarProps) {
  return (
    <>
      {/* Velo sobre el contenido en móvil */}
      <div
        aria-hidden="true"
        onClick={onCloseMobile}
        className={cn(
          'fixed inset-0 z-40 bg-ink/40 transition-opacity duration-200 lg:hidden',
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />

      <aside
        aria-label="Navegación principal"
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r border-line bg-surface',
          'transition-transform duration-200 lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Marca: isotipo + «SellProBot» (el mismo componente que usa la página pública) */}
        <div className="flex h-18 items-center gap-2.5 px-4">
          <BrandLogo className="min-w-0 flex-1" />
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label="Cerrar menú"
            className="flex size-9 items-center justify-center rounded-control text-ink-muted hover:bg-surface-muted lg:hidden"
          >
            <X size={18} />
          </button>
        </div>

        {/* Negocio activo (multi-tenant) */}
        <div className="px-3">
          <TenantSwitcher />
        </div>

        {/* Secciones */}
        <nav className="scroll-thin mt-4 flex-1 overflow-y-auto px-3 pb-4">
          {navSections.map((section, index) => (
            <div key={section.id}>
              {index > 0 ? <div className="my-4 h-px bg-line" /> : null}
              <ul className="space-y-1">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <SidebarLink
                      item={item}
                      onNavigate={onCloseMobile}
                      // La burbuja va donde el usuario la busca: en la fila del inbox.
                      alertas={item.to === ROUTES.conversations ? alertas.nuevas.length : 0}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {env.isDev ? (
          <div className="border-t border-line px-4 py-3">
            <p className="text-micro text-ink-faint">API</p>
            <p className="truncate text-micro text-ink-muted" title={env.apiUrl}>
              {env.apiUrl}
            </p>
          </div>
        ) : null}
      </aside>
    </>
  );
}

function SidebarLink({
  item,
  onNavigate,
  alertas,
}: {
  item: NavItem;
  onNavigate: () => void;
  /** Conversaciones con mensajes sin ver: si es 0, la fila va sin burbuja. */
  alertas: number;
}) {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-control px-3 py-2.5 text-body font-medium transition-colors',
          isActive
            ? 'bg-brand-600 text-white shadow-sm'
            : 'text-ink-muted hover:bg-surface-muted hover:text-ink-soft',
        )
      }
    >
      <Icon size={18} className="shrink-0" />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      <InboxAlertsBubble total={alertas} variant="fila" />
    </NavLink>
  );
}
